import { create } from "zustand";
import { router } from "../data/Services/ordersRouter.jsx";
import { convertRouterOrderToOrderObject } from "../utils/orderConversions.js";
import performanceMonitor from "../utils/performanceMonitor.js";

import { convertReportToOrder } from "./orderReportConversion.js";

// #364 — toggle de runtime (default ON). A perna OTO/OCO de proteção chega no
// broker ~560ms após o fill da entrada; seu `rpt` vem como OrdType=OCO SEM
// limit/stop → convertReportToOrder a rejeita (price=0) e não dá pra renderizar
// (sem preço nem par -L/-S). Usamos a CHEGADA desse rpt como gatilho de refetch
// do accountData, que traz o par -L/-S com preços.
//   Live (pré-pregão): window.__OTO_LEG_REFETCH__ = false (red) / = true (green)
//   URL: ?otoLegRefetch=off (red) | on (green)
const isOtoLegRefetchEnabled = () => {
  if (typeof window === "undefined") return true;
  if (window.__OTO_LEG_REFETCH__ !== undefined)
    return !!window.__OTO_LEG_REFETCH__;
  const p = new URLSearchParams(window.location.search).get("otoLegRefetch");
  if (p === "off" || p === "0") return false;
  if (p === "on" || p === "1") return true;
  return true; // default ON
};

// Extract price updates from a report (for replaced/modified orders)
const extractPriceUpdates = (report) => {
  const updates = {};

  if (report.limit !== undefined && report.limit > 0) {
    updates.limit = report.limit;
    updates.price = report.limit;
  }
  if (report.stop !== undefined && report.stop > 0) {
    updates.stop = report.stop;
    updates.price = report.stop;
  }
  if (report.price !== undefined && report.price > 0 && !updates.price) {
    updates.price = report.price;
  }
  if (
    report.quantity !== undefined ||
    report.qty !== undefined ||
    report.qtt !== undefined
  ) {
    updates.quantity = report.quantity || report.qty || report.qtt;
  }

  return updates;
};

// Stable empty array to prevent infinite re-renders
const EMPTY_ORDERS = [];

// Global initialization flag to prevent multiple initializations
let isInitialized = false;

// Track pending OTO data for orders being submitted (local → router transition).
// #355: LISTA de { orderId, symbol, oto, timestamp } (não Map por símbolo) — assim
// dois brackets rápidos no mesmo símbolo não colidem. Correlação por client-id quando
// disponível (report.clientId), senão FIFO por símbolo. Ver storePendingOTO/consume.
const pendingOTOData = [];
const PENDING_OTO_TTL = 10000;

// OrdersRouter#6 (odin#441): assinatura das pernas OTO + vínculo OCO para o
// change-check de setOrdersForSymbol/batchSetOrdersForSymbols. Sem ela, um
// accountData cuja única novidade são as pernas aninhadas (ordem OTO criada em
// OUTRO cliente — o rpt chega antes e grava a entrada sem oto) era descartado
// como "sem mudança" e o gráfico nunca desenhava GANHO/PERDA. Inclui preços
// para também captar perna arrastada em outro cliente.
const otoSignature = (order) =>
  `${order.oco ?? ""}#${(order.oto || [])
    .map((l) => `${l.id}:${l.limit ?? ""}:${l.stop ?? ""}:${l.price ?? ""}`)
    .join("|")}`;

export const useOrderStore = create((set, get) => ({
  // Store orders by symbol: { "WINFUT": [order1, order2], "PETR4": [order3] }
  ordersBySymbol: {},

  // Track orders with pending modifications (for visual feedback)
  pendingModifications: new Set(),

  // Track orders that have been finalized (prevent resurrection from stale accountData)
  // Key: orderId, Value: { timestamp: number, state: string }
  orderTombstones: new Map(),

  // Set/clear pending modification state for an order
  setPendingModification: (orderId, isPending) => {
    set((state) => {
      const newPending = new Set(state.pendingModifications);
      if (isPending) {
        newPending.add(orderId);
      } else {
        newPending.delete(orderId);
      }
      return { pendingModifications: newPending };
    });
  },

  // Check if an order has a pending modification
  isPendingModification: (orderId) => {
    return get().pendingModifications.has(orderId);
  },

  // Add a tombstone for a finalized order (prevents resurrection from stale accountData)
  addTombstone: (orderId, finalState) => {
    set((state) => {
      const tombstones = new Map(state.orderTombstones);
      tombstones.set(orderId, {
        timestamp: Date.now(),
        state: finalState,
      });
      return { orderTombstones: tombstones };
    });
  },

  // Clean up expired tombstones (> 30 seconds old)
  cleanupTombstones: () => {
    const TOMBSTONE_TTL = 30000; // 30 seconds
    set((state) => {
      const tombstones = new Map(state.orderTombstones);
      const now = Date.now();
      let cleaned = 0;
      for (const [id, data] of tombstones) {
        if (now - data.timestamp > TOMBSTONE_TTL) {
          tombstones.delete(id);
          cleaned++;
        }
      }
      if (cleaned > 0 && import.meta.env.DEV) {
        console.log(`🧹 Tombstones: Cleaned ${cleaned} expired entries`);
      }
      return { orderTombstones: tombstones };
    });
  },

  // Check if order is tombstoned
  isOrderTombstoned: (orderId) => {
    return get().orderTombstones.has(orderId);
  },

  // Add order to a symbol
  addOrder: (symbol, order) => {
    set((state) => ({
      ordersBySymbol: {
        ...state.ordersBySymbol,
        [symbol]: [...(state.ordersBySymbol[symbol] || []), order],
      },
    }));
  },

  // Update specific order by symbol and orderId
  updateOrder: (symbol, orderId, updates) => {
    set((state) => {
      const currentOrders = state.ordersBySymbol[symbol] || [];

      const newOrdersBySymbol = {
        ...state.ordersBySymbol,
        [symbol]: currentOrders.map((order) => {
          if (order.id === orderId) {
            // Simple order update - apply updates to order
            // IMPORTANT: Do NOT preserve order.oto here - OTO updates are handled separately
            // via updateOTOInOrder and we don't want to overwrite those updates with stale data.
            // If updates.oto is provided, use it. Otherwise, keep whatever oto is already on the order.
            return { ...order, ...updates };
          }
          return order;
        }),
      };

      return { ordersBySymbol: newOrdersBySymbol };
    });
  },

  // Remove order by symbol and orderId
  removeOrder: (symbol, orderId) => {
    set((state) => ({
      ordersBySymbol: {
        ...state.ordersBySymbol,
        [symbol]: (state.ordersBySymbol[symbol] || []).filter(
          (order) => order.id !== orderId,
        ),
      },
    }));
  },

  // Set all orders for a symbol (for bulk updates from router)
  setOrdersForSymbol: (symbol, orders) => {
    set((state) => {
      const currentOrders = state.ordersBySymbol[symbol] || [];

      // Quick check: if lengths differ, definitely need to update
      if (currentOrders.length !== orders.length) {
        return {
          ordersBySymbol: {
            ...state.ordersBySymbol,
            [symbol]: orders,
          },
        };
      }

      // Compare order IDs and key properties
      let hasChanges = false;
      for (let i = 0; i < orders.length; i++) {
        const curr = currentOrders[i];
        const next = orders[i];
        if (
          !curr ||
          curr.id !== next.id ||
          curr.price !== next.price ||
          curr.state !== next.state ||
          curr.quantity !== next.quantity ||
          otoSignature(curr) !== otoSignature(next)
        ) {
          hasChanges = true;
          break;
        }
      }

      // Return same state if no changes (prevents unnecessary re-renders)
      if (!hasChanges) {
        return state;
      }

      return {
        ordersBySymbol: {
          ...state.ordersBySymbol,
          [symbol]: orders,
        },
      };
    });
  },

  // Add or update order directly from a report (for instant display)
  addOrUpdateOrderFromReport: (symbol, order) => {
    // FIX #138: Guard against null orders (rejected by convertReportToOrder due to invalid price)
    if (!order || !order.id) {
      console.warn(
        "⚠️ addOrUpdateOrderFromReport: Skipping null/invalid order",
      );
      return;
    }

    set((state) => {
      const currentOrders = state.ordersBySymbol[symbol] || [];
      const existingIndex = currentOrders.findIndex((o) => o.id === order.id);

      let updatedOrders;
      if (existingIndex >= 0) {
        // Update existing order - preserve OTO legs and other data
        updatedOrders = [...currentOrders];
        const existing = updatedOrders[existingIndex];
        updatedOrders[existingIndex] = {
          ...existing,
          ...order,
          // Preserve OTO legs if they exist and aren't in the new data
          oto: order.oto || existing.oto,
        };
      } else {
        // Add new order
        updatedOrders = [...currentOrders, order];
      }

      // Track store update for performance monitoring
      performanceMonitor.trackOrderStoreUpdate();

      return {
        ordersBySymbol: {
          ...state.ordersBySymbol,
          [symbol]: updatedOrders,
        },
      };
    });
  },

  // Remove or mark order as final state (filled, cancelled, rejected)
  // Also adds tombstone to prevent resurrection from stale accountData
  removeOrderFromReport: (symbol, orderId, finalState = null) => {
    // Add tombstone BEFORE removing (blocks stale accountData from resurrecting)
    if (finalState) {
      get().addTombstone(orderId, finalState);
      if (import.meta.env.DEV) {
        console.log(`🪦 Tombstone: Added for ${orderId} (${finalState})`);
      }
    }

    set((state) => {
      const currentOrders = state.ordersBySymbol[symbol] || [];
      const filteredOrders = currentOrders.filter((o) => o.id !== orderId);

      // Only update if order was actually removed
      if (filteredOrders.length === currentOrders.length) {
        return state;
      }

      // Track store update for performance monitoring
      performanceMonitor.trackOrderStoreUpdate();

      return {
        ordersBySymbol: {
          ...state.ordersBySymbol,
          [symbol]: filteredOrders,
        },
      };
    });
  },

  // Store pending OTO data before removing a local order (for local → router transition)
  // #355: guarda {orderId, symbol, oto} numa LISTA FIFO (não Map por símbolo) — dois
  // brackets rápidos no mesmo símbolo não se sobrescrevem mais; cada um vira uma
  // entrada e é consumida em ordem. `orderId` fica só p/ log/futuro (ver consume).
  storePendingOTO: (orderId, symbol, oto) => {
    if (oto && oto.length > 0) {
      pendingOTOData.push({ orderId, symbol, oto, timestamp: Date.now() });
      if (import.meta.env.DEV) {
        console.log("📦 OrderStore: Stored pending OTO:", {
          orderId,
          symbol,
          legs: oto.length,
        });
      }
      // Expurga entradas expiradas (TTL 10s).
      setTimeout(() => {
        const cutoff = Date.now() - PENDING_OTO_TTL;
        for (let i = pendingOTOData.length - 1; i >= 0; i--) {
          if (pendingOTOData[i].timestamp < cutoff) pendingOTOData.splice(i, 1);
        }
      }, PENDING_OTO_TTL);
    }
  },

  // #355: consome a OTO pendente por FIFO no símbolo (a entrada MAIS ANTIGA). Dois
  // brackets rápidos no mesmo símbolo não colidem mais — cada report pega a sua, na
  // ordem de submit (SignalR é ordenado por conexão). INDEPENDENTE do server.
  // [Melhoria futura — correlação precisa por id (Opção A): exigiria o server devolver
  //  o ClOrdID gerado no retorno do placeOrder (o order.id do client não cabe no
  //  ClOrdID de 28 chars). Ver OrdersRouter docs/odin-sight-355-resposta.md.]
  consumePendingOTO: (symbol) => {
    const cutoff = Date.now() - PENDING_OTO_TTL;
    for (let i = pendingOTOData.length - 1; i >= 0; i--) {
      if (pendingOTOData[i].timestamp < cutoff) pendingOTOData.splice(i, 1);
    }
    const idx = pendingOTOData.findIndex((e) => e.symbol === symbol);
    if (idx < 0) return null;
    const [entry] = pendingOTOData.splice(idx, 1);
    if (import.meta.env.DEV) {
      console.log("📦 OrderStore: Consumed pending OTO (fifo):", { symbol });
    }
    return entry.oto;
  },

  // Update order from report (for replaced/modified orders) - preserves OTO/OCO
  updateOrderFromReport: (symbol, orderId, updates) => {
    set((state) => {
      const currentOrders = state.ordersBySymbol[symbol] || [];
      const orderIndex = currentOrders.findIndex((o) => o.id === orderId);

      if (orderIndex === -1) {
        return state; // Order not found, no update needed
      }

      const existingOrder = currentOrders[orderIndex];
      const updatedOrders = [...currentOrders];

      // Merge updates while preserving existing OTO/OCO/other data
      // Update originalRouterOrder with confirmed values from replace reports
      // so subsequent replace operations use current (not stale) values as base
      const updatedOriginalRouterOrder = existingOrder.originalRouterOrder
        ? {
            ...existingOrder.originalRouterOrder,
            ...(updates.limit !== undefined && { limit: updates.limit }),
            ...(updates.stop !== undefined && { stop: updates.stop }),
            ...(updates.price !== undefined && { price: updates.price }),
            ...(updates.quantity !== undefined && {
              quantity: updates.quantity,
            }),
          }
        : existingOrder.originalRouterOrder;

      updatedOrders[orderIndex] = {
        ...existingOrder,
        ...updates,
        // Preserve these fields from existing order
        oto: existingOrder.oto,
        oco: existingOrder.oco,
        originalRouterOrder: updatedOriginalRouterOrder,
        isRouterOrder: existingOrder.isRouterOrder,
      };

      // Track store update for performance monitoring
      performanceMonitor.trackOrderStoreUpdate();

      return {
        ordersBySymbol: {
          ...state.ordersBySymbol,
          [symbol]: updatedOrders,
        },
      };
    });
  },

  // Batch update orders for multiple symbols in a single state update (performance optimization)
  batchSetOrdersForSymbols: (ordersBySymbolUpdates) => {
    set((state) => {
      // Check if any orders actually changed to avoid unnecessary state updates
      let hasChanges = false;
      for (const symbol of Object.keys(ordersBySymbolUpdates || {})) {
        const currentOrders = state.ordersBySymbol[symbol] || [];
        const newOrders = ordersBySymbolUpdates[symbol];

        // Quick length check
        if (currentOrders.length !== newOrders.length) {
          hasChanges = true;
          break;
        }

        // Compare order IDs and key properties
        for (let i = 0; i < newOrders.length; i++) {
          const curr = currentOrders[i];
          const next = newOrders[i];
          if (
            !curr ||
            curr.id !== next.id ||
            curr.price !== next.price ||
            curr.state !== next.state ||
            curr.quantity !== next.quantity ||
            otoSignature(curr) !== otoSignature(next)
          ) {
            hasChanges = true;
            break;
          }
        }
        if (hasChanges) break;
      }

      // Return same state if no changes (prevents unnecessary re-renders)
      if (!hasChanges) {
        return state;
      }

      // Track store update for performance monitoring
      performanceMonitor.trackOrderStoreUpdate();

      return {
        ordersBySymbol: {
          ...state.ordersBySymbol,
          ...ordersBySymbolUpdates,
        },
      };
    });
  },

  // Get orders for a specific symbol - uses stable empty array
  getOrdersForSymbol: (symbol) => {
    const state = get();
    return state.ordersBySymbol[symbol] || EMPTY_ORDERS;
  },

  // Clear all orders for a symbol
  clearOrdersForSymbol: (symbol) => {
    set((state) => ({
      ordersBySymbol: {
        ...state.ordersBySymbol,
        [symbol]: [],
      },
    }));
  },

  // Clear all router-related data (for logout/disconnection)
  clearAllRouterData: () => {
    console.log("🧹 OrderStore: Clearing all router-related data");

    // Reset initialization flag to allow clean re-initialization
    isInitialized = false;

    set((state) => {
      // Keep only local orders (orders that have isLocal: true)
      const localOrdersBySymbol = {};
      let routerOrderCount = 0;

      Object.keys(state.ordersBySymbol).forEach((symbol) => {
        const symbolOrders = state.ordersBySymbol[symbol];
        const localOrders = symbolOrders.filter((order) => {
          // Keep orders that are explicitly marked as local OR have state: 'local'
          if (order.isLocal === true || order.state === "local") {
            return true; // Keep local orders
          } else {
            routerOrderCount++;
            return false; // Remove router orders
          }
        });

        if (localOrders.length > 0) {
          localOrdersBySymbol[symbol] = localOrders;
        }
      });

      console.log(
        "🧹 OrderStore: Kept",
        Object.keys(localOrdersBySymbol).length,
        "symbols with local orders",
      );
      console.log("🧹 OrderStore: Cleared", routerOrderCount, "router orders");
      console.log("🧹 OrderStore: Reset initialization flag for clean restart");

      return {
        ordersBySymbol: localOrdersBySymbol,
      };
    });
  },

  // Convert router order data to overlay format
  processRouterOrders: (accountData) => {
    if (!accountData || !accountData.openOrders) return {};

    const ordersBySymbol = {};

    Object.keys(accountData.openOrders).forEach((symbol) => {
      const symbolOrders = accountData.openOrders[symbol];
      if (Array.isArray(symbolOrders)) {
        ordersBySymbol[symbol] = symbolOrders.map((routerOrder) => {
          // Use the shared conversion to ensure consistent OTO handling & schema
          const converted = convertRouterOrderToOrderObject(
            routerOrder,
            symbol,
          );
          // Normalize state using existing mapper (shared util already handles but keep for parity)
          converted.state = mapRouterState(
            routerOrder.status || routerOrder.state || converted.state,
          );

          return converted;
        });
      }
    });
    return ordersBySymbol;
  },

  // OTO-related methods

  // Add OTO order to a main order
  addOTOToOrder: (symbol, mainOrderId, otoOrder) => {
    set((state) => ({
      ordersBySymbol: {
        ...state.ordersBySymbol,
        [symbol]: (state.ordersBySymbol[symbol] || []).map((order) =>
          order.id === mainOrderId
            ? { ...order, oto: [...(order.oto || []), otoOrder] }
            : order,
        ),
      },
    }));
  },

  // Update OTO order in a main order
  updateOTOInOrder: (symbol, mainOrderId, otoOrderId, updates) => {
    set((state) => {
      const newOrdersBySymbol = {
        ...state.ordersBySymbol,
        [symbol]: (state.ordersBySymbol[symbol] || []).map((order) =>
          order.id === mainOrderId
            ? {
                ...order,
                oto: (order.oto || []).map((otoOrder) =>
                  otoOrder.id === otoOrderId
                    ? { ...otoOrder, ...updates }
                    : otoOrder,
                ),
              }
            : order,
        ),
      };

      return { ordersBySymbol: newOrdersBySymbol };
    });
  },

  // Remove OTO order from a main order
  removeOTOFromOrder: (symbol, mainOrderId, otoOrderId) => {
    set((state) => ({
      ordersBySymbol: {
        ...state.ordersBySymbol,
        [symbol]: (state.ordersBySymbol[symbol] || []).map((order) =>
          order.id === mainOrderId
            ? {
                ...order,
                oto: (order.oto || []).filter(
                  (otoOrder) => otoOrder.id !== otoOrderId,
                ),
              }
            : order,
        ),
      },
    }));
  },

  // Initialize router integration (called once per hook instance)
  initialize: () => {
    // Prevent multiple initializations
    if (isInitialized) {
      return () => {}; // Return empty cleanup function
    }

    isInitialized = true;
    console.log("OrderStore: Initializing router integration...");

    const {
      processRouterOrders,
      batchSetOrdersForSymbols,
      getOrdersForSymbol,
    } = get();

    const handleAccountDataUpdate = (accountData) => {
      // Cleanup expired tombstones first
      get().cleanupTombstones();

      // Get router orders by symbol
      const routerOrdersBySymbol = processRouterOrders(accountData);

      // Get current tombstones to filter ghost orders
      const tombstones = get().orderTombstones;

      // Build all updates first, then batch apply (single state update)
      const batchUpdates = {};

      // Get current state to find symbols that might need cleanup
      const currentOrdersBySymbol = get().ordersBySymbol;

      // Process symbols that exist in router response
      Object.keys(routerOrdersBySymbol).forEach((symbol) => {
        let routerOrders = routerOrdersBySymbol[symbol];

        // 🪦 FILTER OUT TOMBSTONED ORDERS (prevents ghost resurrection from stale accountData)
        const originalCount = routerOrders.length;
        routerOrders = routerOrders.filter((order) => {
          if (tombstones.has(order.id)) {
            if (import.meta.env.DEV) {
              console.log(`👻 Blocked ghost resurrection: ${order.id}`);
            }
            return false;
          }
          return true;
        });

        if (routerOrders.length < originalCount && import.meta.env.DEV) {
          console.log(
            `🛡️ Tombstone protection: Blocked ${originalCount - routerOrders.length} ghost orders for ${symbol}`,
          );
        }

        const currentOrders = getOrdersForSymbol(symbol);

        // Merge with local orders, preferring router state for existing orders
        const merged = [...currentOrders];

        // Update existing orders with router data
        routerOrders.forEach((routerOrder) => {
          const existingIndex = merged.findIndex(
            (o) => o.id === routerOrder.id,
          );
          if (existingIndex >= 0) {
            const existing = merged[existingIndex];
            // Preserve existing OTO legs if router snapshot omits them
            if (existing.oto && !routerOrder.oto) {
              routerOrder.oto = existing.oto;
            }
            merged[existingIndex] = routerOrder;
          } else {
            merged.push(routerOrder);
          }
        });

        // Remove orders that are no longer in router (filled/cancelled)
        // But keep ALL local orders regardless of router state
        const filtered = merged.filter((order) => {
          if (order.state === "local") {
            return true;
          }
          return routerOrders.some((ro) => ro.id === order.id);
        });

        batchUpdates[symbol] = filtered;
      });

      // FIX #126: Clean up symbols that exist in current state but NOT in router response
      // This handles the case where ALL orders for a symbol were cancelled
      // Without this, ghost orders remain when a symbol disappears from openOrders
      Object.keys(currentOrdersBySymbol).forEach((symbol) => {
        if (!routerOrdersBySymbol[symbol]) {
          // Symbol no longer has any orders in router - remove all non-local orders
          const currentOrders = currentOrdersBySymbol[symbol] || [];
          const localOnlyOrders = currentOrders.filter(
            (order) => order.state === "local",
          );

          // Only update if there were non-local orders to remove
          if (localOnlyOrders.length !== currentOrders.length) {
            const ghostOrderCount =
              currentOrders.length - localOnlyOrders.length;
            console.log(
              `👻 FIX #126: Symbol ${symbol} not in router - removing ${ghostOrderCount} ghost orders`,
            );
            batchUpdates[symbol] = localOnlyOrders;
          }
        }
      });

      // Single state update for all symbols
      if (Object.keys(batchUpdates).length > 0) {
        batchSetOrdersForSymbols(batchUpdates);
      }
    };

    // Debounced accountData refresh to avoid flooding server with requests
    let accountDataRefreshTimeout = null;
    const scheduleAccountDataRefresh = (immediate = false) => {
      if (accountDataRefreshTimeout) {
        clearTimeout(accountDataRefreshTimeout);
        accountDataRefreshTimeout = null;
      }

      const doRefresh = () => {
        accountDataRefreshTimeout = null;
        if (router.state === "online") {
          console.log(
            "📊 OrderStore: Requesting fresh accountData after order status change",
          );
          // Clear the cache to force processing even if backend data is identical
          // This ensures frontend ghost orders get cleaned up
          router._lastAccountDataString = null;
          router.getAccountDataAsync();
        }
      };

      if (immediate) {
        // Use queueMicrotask for immediate execution (not throttled by browsers)
        // This ensures cross-machine updates are processed promptly
        queueMicrotask(doRefresh);
      } else {
        accountDataRefreshTimeout = setTimeout(doRefresh, 300); // Wait 300ms for reports to settle
      }
    };

    // #364 — Um `rpt` open/new que convertReportToOrder não consegue renderizar
    // (price=0) sinaliza uma ordem que o snapshot ainda não tem. O caso que importa
    // é a perna OTO/OCO de proteção (rpt do OCO vem sem limit/stop → só o par -L/-S
    // do accountData tem os preços). Dispara um refetch debounced p/ puxá-la.
    // NB: NÃO é OCO-exclusivo — uma ordem a mercado (e stop-market) também emite um
    // New sem preço antes do fill → dispara aqui; é benigno (refetch read-only,
    // redundante com o refetch que o próprio fill já faz). Guard de id evita
    // re-disparo a cada rpt seguinte da mesma perna (o id base nunca casa com -L/-S).
    const otoRefetchGuard = new Set();
    const triggerOcoLegRefetch = (report) => {
      if (!isOtoLegRefetchEnabled()) return;
      const key = report?.id;
      if (!key || otoRefetchGuard.has(key)) return;
      otoRefetchGuard.add(key);
      setTimeout(() => otoRefetchGuard.delete(key), 10_000); // TTL do guard
      if (import.meta.env.DEV) {
        console.info(
          "[#364] rpt sem preço (provável perna OCO) → refetch p/ par -L/-S:",
          key,
        );
      }
      scheduleAccountDataRefresh(); // debounced (coalesce múltiplas pernas)
    };

    // Get store actions for direct order manipulation
    const {
      addOrUpdateOrderFromReport,
      removeOrderFromReport,
      updateOrderFromReport,
    } = get();

    // Helper to find existing order by ID across all symbols (when report.symbol is null)
    const findOrderById = (orderId) => {
      const state = get();
      for (const [sym, orders] of Object.entries(state.ordersBySymbol)) {
        const order = orders.find((o) => o.id === orderId);
        if (order) return { order, symbol: sym };
      }
      return null;
    };

    // Register a single receiver object that handles both account data and reports
    const receiver = {
      receiveAccountData: handleAccountDataUpdate,
      receiveReport: (report) => {
        try {
          if (!report || !report.id) return;

          const status = report?.OrdStatus || report?.state;
          const reportSymbol = report?.symbol;
          const orderId = report.id;

          // FIX #138 DEBUG: Log raw report data to verify router sends incomplete price data
          if (import.meta.env.DEV) {
            const hasValidPrice =
              (report.limit && report.limit > 0) ||
              (report.stop && report.stop > 0) ||
              (report.price && report.price > 0);
            console.log("📋 OrderStore: Raw report received:", {
              id: orderId,
              symbol: reportSymbol,
              status: status,
              limit: report.limit,
              stop: report.stop,
              price: report.price,
              quantity: report.quantity || report.qty || report.qtt,
              hasValidPrice,
              // Flag potential ghost order source
              potentialGhostSource:
                !hasValidPrice &&
                (status === "open" ||
                  status === "active" ||
                  status === "1" ||
                  status === "5"),
            });
          }

          // Categorize the report state
          const isNewState = status === "0" || status === "new";
          const isOpenState =
            status === "1" ||
            status === "5" ||
            status === "open" ||
            status === "active" ||
            status === "pending";
          // Server sends OrdStatus 1 as " partialFilled" (leading space, camelCase)
          // — normalize trim+case so partial fills are recognized (not "partial").
          const isPartialState =
            status === "partial" ||
            (typeof status === "string" &&
              status.trim().toLowerCase() === "partialfilled");
          const isReplacedState = status === "replaced";
          const isFinalState =
            status === "4" ||
            status === "2" ||
            status === "8" ||
            status === "cancelled" ||
            status === "filled" ||
            status === "rejected";

          // Handle NEW orders - add to store immediately
          if (isNewState && reportSymbol) {
            // #441 — a ordem nascida em OUTRO cliente (plataforma) chega aqui como rpt
            // da ENTRADA, pelado: sem `oto` (só o accountData monta as pernas, via
            // ADOpenOrder.GetFromOTO) e COM preço — logo não casa nenhum dos dois campos
            // do gatilho #364 abaixo. Sem refetch, as pernas GANHO/PERDA nunca chegam e
            // o gráfico fica só com a entrada até algo mais forçar um snapshot (F5,
            // arrastar, cancelar). `knownBefore` precisa ser lido ANTES do add: depois
            // dele o store já conhece a ordem e o sinal se perde.
            const knownBefore = !!findOrderById(orderId);
            let attachedLocalOto = false;
            const order = convertReportToOrder(report, reportSymbol);
            if (order) {
              // Check for pending OTO data from recently submitted local orders.
              // Guard !report.oco: perna OCO (contrato novo: -L/-S priced c/ `oco`) não
              // consome pendingOTO — traz limit/stop próprios; pendingOTO é overlay da
              // ENTRADA. Evita roubar o pendingOTO FIFO de outra entrada no símbolo.
              const pendingOTO = report.oco
                ? null
                : get().consumePendingOTO(reportSymbol);
              if (pendingOTO) {
                attachedLocalOto = true;
                order.oto = pendingOTO;
                // Also update originalRouterOrder with OTO
                if (order.originalRouterOrder) {
                  order.originalRouterOrder.oto = pendingOTO;
                }
                if (import.meta.env.DEV) {
                  const gainLeg = pendingOTO.find(
                    (leg) =>
                      leg.type === "profit" ||
                      (leg.orderType === "LIMIT" && !leg.stop),
                  );
                  const lossLeg = pendingOTO.find(
                    (leg) =>
                      leg.type === "stop" || leg.orderType === "STOPLIMIT",
                  );
                  const gainPrice = gainLeg
                    ? gainLeg.price || gainLeg.limit
                    : null;
                  const lossPrice = lossLeg
                    ? lossLeg.price || lossLeg.stop
                    : null;
                  console.log(
                    "⚡ OrderStore: New order with pending OTO:",
                    order.id,
                    pendingOTO.length,
                    "legs",
                    gainPrice ? `| Gain: ${gainPrice}` : "",
                    lossPrice ? `| Loss: ${lossPrice}` : "",
                  );
                }
              }
              addOrUpdateOrderFromReport(reportSymbol, order);
            }
            // #364 — o painel (OpenOrdersPanel) é accountData-driven; a perna OCO só
            // entra nele via refetch. GATILHO em campos do CONTRATO (NÃO no `price<=0`
            // do guard #138 — `price=0` pode ser ordem a mercado válida no futuro):
            //   report.oco                → perna OCO priced (router NOVO)
            //   limit==null && stop==null → price-less (perna OCO do router VELHO, sem
            //                               `oco`; ou New de mercado/stop antes do fill —
            //                               benigno). `== null` cobre null e undefined.
            // Debounce coalesce as 2 pernas -L/-S num só refetch. Cleanup pós-deploy do
            // router novo em todo lugar: estreitar p/ `report.oco` só.
            //
            // #441 — 3º gatilho: ordem ABERTA que o store ainda não conhecia e cujas
            // pernas NÃO vieram de um submit local (`attachedLocalOto`). É o caso
            // cross-client. Deliberadamente NÃO tenta farejar "isto é OTO" no rpt: o
            // report não tem marcador de estrutura (só o accountData tem `oto`), e
            // inferir por preço/lado seria adivinhação. Ordem externa sem OTO nenhuma
            // também dispara — benigno e já assumido acima (refetch read-only, com
            // debounce + guard de id).
            if (
              report.oco ||
              (report.limit == null && report.stop == null) ||
              (!knownBefore && !attachedLocalOto)
            ) {
              triggerOcoLegRefetch(report);
            }
            return;
          }

          // Handle OPEN/ACTIVE/PENDING orders - update or add
          if ((isOpenState || isPartialState) && reportSymbol) {
            // #441 — mesmo racional da branch New (ver comentário lá): lido ANTES do add.
            const knownBefore = !!findOrderById(orderId);
            let attachedLocalOto = false;
            const order = convertReportToOrder(report, reportSymbol);
            if (order) {
              // Check if this is a NEW order (not already in store) - attach pending OTO if any
              const existing = findOrderById(orderId);
              // Guard !report.oco: perna OCO nunca consome pendingOTO (ver branch New).
              if (!existing && !report.oco) {
                const pendingOTO = get().consumePendingOTO(reportSymbol);
                if (pendingOTO) {
                  attachedLocalOto = true;
                  order.oto = pendingOTO;
                  if (order.originalRouterOrder) {
                    order.originalRouterOrder.oto = pendingOTO;
                  }
                  if (import.meta.env.DEV) {
                    const gainLeg = pendingOTO.find(
                      (leg) =>
                        leg.type === "profit" ||
                        (leg.orderType === "LIMIT" && !leg.stop),
                    );
                    const lossLeg = pendingOTO.find(
                      (leg) =>
                        leg.type === "stop" || leg.orderType === "STOPLIMIT",
                    );
                    const gainPrice = gainLeg
                      ? gainLeg.price || gainLeg.limit
                      : null;
                    const lossPrice = lossLeg
                      ? lossLeg.price || lossLeg.stop
                      : null;
                    console.log(
                      "⚡ OrderStore: New open order with pending OTO:",
                      order.id,
                      pendingOTO.length,
                      "legs",
                      gainPrice ? `| Gain: ${gainPrice}` : "",
                      lossPrice ? `| Loss: ${lossPrice}` : "",
                    );
                  }
                }
              }
              addOrUpdateOrderFromReport(reportSymbol, order);
            }
            // #364 — gatilho em campos do contrato (ver branch New):
            // report.oco || (limit == null && stop == null).
            // #441 — + ordem externa desconhecida (ver branch New).
            if (
              report.oco ||
              (report.limit == null && report.stop == null) ||
              (!knownBefore && !attachedLocalOto)
            ) {
              triggerOcoLegRefetch(report);
            }
            return;
          }

          // Handle REPLACED orders - update existing order with new price/quantity
          // Note: report.symbol may be null for replaced orders
          if (isReplacedState) {
            // Find the existing order
            const existing = findOrderById(orderId);
            if (existing) {
              const updates = extractPriceUpdates(report);
              if (Object.keys(updates).length > 0) {
                if (import.meta.env.DEV) {
                  console.log(
                    "🔄 OrderStore: Replaced order update:",
                    orderId,
                    updates,
                  );
                }
                updateOrderFromReport(existing.symbol, orderId, updates);
              }
            } else {
              // OCO: backend sends replace reports with the base order ID (no -L/-S suffix).
              // The store has the individual legs with suffixed IDs — update both.
              const existingL = findOrderById(orderId + "-L");
              const existingS = findOrderById(orderId + "-S");
              if (existingL || existingS) {
                const updates = extractPriceUpdates(report);
                if (Object.keys(updates).length > 0) {
                  if (import.meta.env.DEV) {
                    console.log(
                      "🔄 OrderStore: OCO replaced - updating both legs:",
                      orderId,
                      updates,
                    );
                  }
                  if (existingL)
                    updateOrderFromReport(
                      existingL.symbol,
                      orderId + "-L",
                      updates,
                    );
                  if (existingS)
                    updateOrderFromReport(
                      existingS.symbol,
                      orderId + "-S",
                      updates,
                    );
                }
              } else if (import.meta.env.DEV) {
                console.log(
                  "⚠️ OrderStore: Replaced order not found in store:",
                  orderId,
                );
              }
            }
            return;
          }

          // Handle FINAL states (cancelled, filled, rejected) - remove immediately
          if (isFinalState) {
            // Try to find the order if symbol is missing
            const existingOrderInfo = findOrderById(orderId);
            const symbol = reportSymbol || existingOrderInfo?.symbol;

            // OCO base-ID handling needs the suffixed legs up front (also read by
            // the modification-reject guard below).
            const existingL = findOrderById(orderId + "-L");
            const existingS = findOrderById(orderId + "-S");

            // REJECTED = distinguish a NEW-order rejection (remove) from a
            // MODIFICATION rejection (replace/cancel failed → order still live at
            // broker, keep it). Covers single orders AND OCO suffixed legs: for an
            // OCO modify-reject the report carries the BASE id, so existingOrderInfo
            // is null and we must check the LEGS' state — otherwise the OCO branch
            // below would remove+tombstone both legs that are still working on the
            // broker (órfãs: somem da tela, vivas no broker).
            if (status.toLowerCase() === "rejected") {
              const singleOpen =
                existingOrderInfo && existingOrderInfo.order.state === "open";
              const legOpen =
                !existingOrderInfo &&
                ((existingL && existingL.order.state === "open") ||
                  (existingS && existingS.order.state === "open"));
              if (singleOpen || legOpen) {
                console.warn(
                  "⚠️ OrderStore: Modification rejected, keeping order(s):",
                  orderId,
                );
                // Clear any pending modification state; resync from broker.
                // Don't remove, don't tombstone — order(s) still active on broker.
                get().setPendingModification(orderId, false);
                scheduleAccountDataRefresh();
                return;
              }
            }

            // OCO: backend sends final-state reports with the base order ID (no
            // -L/-S suffix) AND a populated symbol (confirmed: OrdersRouter
            // HubTypes.cs sets symbol = rpt.Symbol unconditionally). The store
            // only has the suffixed legs, so we discriminate on whether the BASE
            // id is a real order — NOT on `symbol` (which is always present).
            // Mirror the replace path: base id found → remove base; otherwise the
            // suffixed legs.
            if (!existingOrderInfo && (existingL || existingS)) {
              if (import.meta.env.DEV) {
                console.log(
                  "🗑️ OrderStore: OCO final state - removing both legs:",
                  orderId,
                  status,
                );
              }
              if (existingL)
                removeOrderFromReport(existingL.symbol, orderId + "-L", status);
              if (existingS)
                removeOrderFromReport(existingS.symbol, orderId + "-S", status);
            } else if (symbol) {
              if (import.meta.env.DEV) {
                console.log(
                  "🗑️ OrderStore: Removing order (final state):",
                  orderId,
                  status,
                );
              }
              // Pass finalState to create tombstone (prevents ghost resurrection)
              removeOrderFromReport(symbol, orderId, status);
            }

            // Refresh accountData immediately to ensure cross-machine sync
            // Use immediate=true to bypass browser timer throttling on unfocused windows
            scheduleAccountDataRefresh(true);
          }
        } catch (error) {
          console.error("📋 OrderStore: Error processing report:", error);
        }
      },
    };

    router.registerReceiver(receiver);

    if (router.state === "online") {
      console.log(
        "📞 OrderStore: Calling getAccountDataAsync during initialization",
      );
      router.getAccountDataAsync();
    }

    // Return cleanup function
    return () => {
      console.log("OrderStore: Cleanup called, unregistering receivers...");
      router.unregisterReceiver(receiver);
      isInitialized = false;
    };
  },
}));

// Wire router-disconnect cleanup at module load: ordersRouter must not import
// this store (import cycle), so clearRouterData() reaches us via this single-slot
// callback instead. Module scope (not initialize()) keeps today's semantics —
// the clear works whenever this module exists, independent of any React mount.
// Optional call: test mocks of ordersRouter may not stub onRouterDataCleared;
// the real Router always has it (characterization: routerClearSeam.test.js).
router.onRouterDataCleared?.(() =>
  useOrderStore.getState().clearAllRouterData(),
);

// Seam de teste (mesma guarda de `window.__odin_router` em ordersRouter.jsx).
//
// Um e2e NÃO alcança este singleton por `import()` dinâmico: medido em
// 2026-07-27, o especificador do teste pode resolver para uma CÓPIA do módulo,
// com o estado inicial e sem o wiring do app. Injetar ordens na cópia não
// chega ao gráfico, e o teste falha desenhando nada. Fora de produção,
// expõe o store real para os specs que injetam estado client-side.
if (typeof window !== "undefined" && import.meta.env.MODE !== "production") {
  window.__ORDER_STORE__ = useOrderStore;
}

// Map router status to our standardized state enum
// Always use 'open' for active orders - no more 'active' vs 'open' conversions
const mapRouterState = (routerStatus) => {
  const status = routerStatus?.toLowerCase() || "";
  switch (status) {
    case "0":
    case "1":
    case "5":
    case "pending":
      return "pending";
    case "active":
    case "open":
      return "open"; // Standardized: always 'open' for active orders
    case "filled":
      return "filled";
    case "cancelled":
      return "cancelled";
    case "rejected":
      return "rejected";
    default:
      return "open";
  }
};
